import { createFileRoute } from '@tanstack/react-router'
import { Link, useIsRTL, useTranslation } from '@spcc/core'
import type { TFunction } from 'i18next'
import { getTextDirection } from '@/lib/text-direction'

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
})

type PrivacySection = {
  id: string
  title: string
  body: string[]
  items: string[]
}

type PrivacyLink = {
  to: string
  labelKey: string
}

const PRIVACY_LINKS: PrivacyLink[] = [
  { to: '/', labelKey: 'common.backToHome' },
  { to: '/login', labelKey: 'privacy.backToLogin' },
]

function getList(t: TFunction, key: string): string[] {
  const value = t(key, { returnObjects: true }) as unknown
  return Array.isArray(value) ? value : []
}

function buildPrivacySections(t: TFunction): PrivacySection[] {
  return [
    {
      id: 'overview',
      title: t('privacy.sections.overview.title'),
      body: [t('privacy.sections.overview.body')],
      items: [],
    },
    {
      id: 'collection',
      title: t('privacy.sections.collection.title'),
      body: [],
      items: getList(t, 'privacy.sections.collection.items'),
    },
    {
      id: 'usage',
      title: t('privacy.sections.usage.title'),
      body: [],
      items: getList(t, 'privacy.sections.usage.items'),
    },
    {
      id: 'sharing',
      title: t('privacy.sections.sharing.title'),
      body: [],
      items: getList(t, 'privacy.sections.sharing.items'),
    },
    {
      id: 'retention',
      title: t('privacy.sections.retention.title'),
      body: [t('privacy.sections.retention.body')],
      items: [],
    },
    {
      id: 'security',
      title: t('privacy.sections.security.title'),
      body: [t('privacy.sections.security.body')],
      items: [],
    },
    {
      id: 'rights',
      title: t('privacy.sections.rights.title'),
      body: [],
      items: getList(t, 'privacy.sections.rights.items'),
    },
    {
      id: 'cookies',
      title: t('privacy.sections.cookies.title'),
      body: [t('privacy.sections.cookies.body')],
      items: [],
    },
    {
      id: 'children',
      title: t('privacy.sections.children.title'),
      body: [t('privacy.sections.children.body')],
      items: [],
    },
    {
      id: 'changes',
      title: t('privacy.sections.changes.title'),
      body: [t('privacy.sections.changes.body')],
      items: [],
    },
    {
      id: 'contact',
      title: t('privacy.sections.contact.title'),
      body: [t('privacy.sections.contact.body')],
      items: [],
    },
  ]
}

function renderParagraphs(sectionId: string, paragraphs: string[]): JSX.Element[] {
  return paragraphs.map((paragraph, index) => (
    <p key={`${sectionId}-p-${index}`} className="text-sm md:text-base text-muted-foreground leading-relaxed">
      {paragraph}
    </p>
  ))
}

function renderItems(sectionId: string, items: string[]): JSX.Element | null {
  if (items.length === 0) return null

  return (
    <ul className="mt-4 space-y-2 list-disc ps-5 text-sm md:text-base text-muted-foreground">
      {items.map((item, index) => (
        <li key={`${sectionId}-i-${index}`}>{item}</li>
      ))}
    </ul>
  )
}

function renderPrivacySections(sections: PrivacySection[]): JSX.Element[] {
  return sections.map((section) => (
    <section
      key={section.id}
      className="rounded-2xl border border-border/60 bg-card/70 p-6 md:p-8 shadow-sm"
    >
      <div className="space-y-4">
        <h2 className="text-lg md:text-2xl font-semibold text-foreground">{section.title}</h2>
        {renderParagraphs(section.id, section.body)}
        {renderItems(section.id, section.items)}
      </div>
    </section>
  ))
}

function renderPrivacyLinks(t: TFunction): JSX.Element[] {
  return PRIVACY_LINKS.map((link) => (
    <Link
      key={link.to}
      to={link.to}
      className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
    >
      {t(link.labelKey)}
    </Link>
  ))
}

function PrivacyPage() {
  const { t } = useTranslation()
  const isRTL = useIsRTL()
  const sections = buildPrivacySections(t)
  const direction = getTextDirection(isRTL)

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5" dir={direction}>
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5" />

      <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-16 pt-10 sm:px-6 sm:pt-14 lg:px-10">
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {renderPrivacyLinks(t)}
        </div>

        <header className="mt-8 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {t('privacy.subtitle')}
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">{t('privacy.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('privacy.lastUpdated')}</p>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{t('privacy.intro')}</p>
        </header>

        <div className="mt-10 space-y-6">
          {renderPrivacySections(sections)}
        </div>
      </div>
    </div>
  )
}
